import { createDecorator } from "../../platform/instantiation/common/registry.js";
import { Emitter, Event } from "./events.js";
import { Disposable, IDisposable } from "./lifecycle.js";

export interface IActionChangeEvent {
    readonly label?: string;
    readonly tooltip?: string;
    readonly class?: string;
    readonly enabled?: boolean;
    readonly checked?: boolean;
    readonly icon?: string;
    readonly hidden?: boolean;
}

export interface IAction {
    readonly id: string;
    label: string;
    tooltip: string;
    enabled: boolean;
    checked?: boolean;
    icon?: string;
    hidden?: boolean;
    run(...args: unknown[]): unknown;
    readonly onDidChange: Event<IActionChangeEvent>;
}

// Used by the menu
export type IActionDescription = {
    id: string;
    label?: string;
    tooltip?: string;
    icon?: string;
    enabled?: boolean;
    checked?: boolean;
    run?(...args: unknown[]): unknown;
}

export interface IRunEvent {
    readonly action: IAction;
    readonly error?: Error;
}

export const IActionRunner = createDecorator<IActionRunner>('actionRunner');

export interface IActionRunner extends IDisposable {
    readonly onDidRun: Event<IRunEvent>;
    readonly onWillRun: Event<IRunEvent>;

    run(action: IAction, context?: unknown): unknown;
}


// #region Actions

/**
 * A concrete implementation of {@link IAction}.
 *
 * Note that in most cases you should use the lighter-weight {@linkcode toAction} function instead.
 */
export class Action extends Disposable implements IAction {

    protected _onDidChange = this._register(new Emitter<IActionChangeEvent>());
    get onDidChange() { return this._onDidChange.event; }

    protected readonly _id: string;
    protected _label: string;
    protected _tooltip: string | undefined;
    protected _enabled: boolean = true;
    protected _checked?: boolean;
    protected _hidden?: boolean;
    protected readonly _actionCallback?: (event?: unknown) => unknown;

    constructor(id: string, label: string = '', enabled: boolean = true, actionCallback?: (event?: unknown) => unknown) {
        super();
        this._id = id;
        this._label = label;
        this._enabled = enabled;
        this._actionCallback = actionCallback;
    }

    get id(): string {
        return this._id;
    }

    get label(): string {
        return this._label;
    }

    set label(value: string) {
        this._setLabel(value);
    }

    private _setLabel(value: string): void {
        if (this._label !== value) {
            this._label = value;
            this._onDidChange.fire({ label: value });
        }
    }

    get tooltip(): string {
        return this._tooltip || '';
    }

    set tooltip(value: string) {
        this._setTooltip(value);
    }

    protected _setTooltip(value: string): void {
        if (this._tooltip !== value) {
            this._tooltip = value;
            this._onDidChange.fire({ tooltip: value });
        }
    }

    get enabled(): boolean {
        return this._enabled;
    }

    set enabled(value: boolean) {
        this._setEnabled(value);
    }

    protected _setEnabled(value: boolean): void {
        if (this._enabled !== value) {
            this._enabled = value;
            this._onDidChange.fire({ enabled: value });
        }
    }

    get checked(): boolean | undefined {
        return this._checked;
    }

    set checked(value: boolean | undefined) {
        this._setChecked(value);
    }

    protected _setChecked(value: boolean | undefined): void {
        if (this._checked !== value) {
            this._checked = value;
            this._onDidChange.fire({ checked: value });
        }
    }

    get icon(): string | undefined {
        return this._icon;
    }
    set icon(value: string | undefined) {
        this._setIcon(value);
    }

    protected _icon: string | undefined;
    protected _setIcon(value: string | undefined): void {
        if (this._icon !== value) {
            this._icon = value;
            this._onDidChange.fire({ icon: value });
        }
    }

    get hidden(): boolean | undefined {
        return this._hidden;
    }
    set hidden(value: boolean | undefined) {
        this._setHidden(value);
    }
    protected _setHidden(value: boolean | undefined): void {
        if (this._hidden !== value) {
            this._hidden = value;
            this._onDidChange.fire({ hidden: value });
        }   
    }

    async run(event?: unknown): Promise<unknown> {
        if (this._actionCallback) {
            return await this._actionCallback(event);
        }
    }
}

export class SubmenuAction implements IAction {

    private _onDidChange = new Emitter<IActionChangeEvent>();
    readonly onDidChange = this._onDidChange.event;

    readonly id: string;
    readonly label: string;
    readonly class: string | undefined;
    readonly tooltip: string = '';
    readonly enabled: boolean = true;
    readonly checked: undefined = undefined;

    private readonly _actions: readonly IAction[];
    get actions(): readonly IAction[] { return this._actions; }

    addAction(action: IAction) {
        const list = this._actions as IAction[];
        if (list.includes(action)) {
            return;
        }
        list.push(action);
    }

    constructor(id: string, label: string, actions: readonly IAction[], cssClass?: string) {
        this.id = id;
        this.label = label;
        this.class = cssClass;
        this._actions = actions;
    }

    async run(): Promise<void> { }
}



// #region Action Runner

export class ActionRunner extends Disposable implements IActionRunner {

    private readonly _onWillRun = this._register(new Emitter<IRunEvent>());
    get onWillRun() { return this._onWillRun.event; }

    private readonly _onDidRun = this._register(new Emitter<IRunEvent>());
    get onDidRun() { return this._onDidRun.event; }

    async run(action: IAction, context?: unknown): Promise<void> {
        if (!action.enabled) {
            return;
        }

        this._onWillRun.fire({ action });

        let error: Error | undefined = undefined;
        try {
            await this.runAction(action, context);
        } catch (e) {
            error = e;
        }

        this._onDidRun.fire({ action, error });
    }

    protected async runAction(action: IAction, context?: unknown): Promise<void> {
        await action.run(context);
    }
}


export class Separator implements IAction {

	/**
	 * Joins all non-empty lists of actions with separators.
	 */
	public static join(...actionLists: readonly IAction[][]) {
		let out: IAction[] = [];
		for (const list of actionLists) {
			if (!list.length) {
				// skip
			} else if (out.length) {
				out = [...out, new Separator(), ...list];
			} else {
				out = list;
			}
		}

		return out;
	}

	static readonly ID = 'vs.actions.separator';

	readonly id: string = Separator.ID;

	readonly label: string = '';
	readonly tooltip: string = '';
	readonly class: string = 'separator';
	readonly enabled: boolean = false;
	readonly checked: boolean = false;
    readonly onDidChange: Event<IActionChangeEvent> = Event.None;
	async run() { }
}





export function toAction(props: { id: string; label: string; tooltip?: string; enabled?: boolean; checked?: boolean; class?: string; run: Function }): IAction {
	return {
		id: props.id,
		label: props.label,
		tooltip: props.tooltip ?? props.label,
		// class: props.class,
		enabled: props.enabled ?? true,
		checked: props.checked,
		run: async (...args: unknown[]) => props.run(...args),
        onDidChange: Event.None
	};
}
